import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router';
import { skipToken } from '@reduxjs/toolkit/query';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import { CLOSE_DIALOG_EVENT } from '@rio-cloud/rio-uikit/FrameDialog';
import usePostMessageSender from '@rio-cloud/rio-uikit/usePostMessageSender';

import { MFE_DIALOG_PREFIX } from '@/features/app/dialogs/dialogConstants';
import DialogErrorState from '@/features/app/dialogs/DialogErrorState';
import DialogFooter from '@/features/app/dialogs/DialogFooter';
import DialogLoadingState from '@/features/app/dialogs/DialogLoadingState';
import { useGetAssignmentQuery } from '@/services/api';
import ContactPerson from './ContactPerson';

const renderAddressLine = (text?: string) => (text ? <div>{text.trim()}</div> : '-');

const CustomerDetailsDialog = () => {
    const { assetId } = useParams<{ assetId: string }>();
    const { sendMessageToParent } = usePostMessageSender();

    const { data: assignment, isLoading, isError, refetch, isFetching } = useGetAssignmentQuery(assetId ?? skipToken);

    const handleCloseDialog = () => {
        sendMessageToParent({
            type: `${MFE_DIALOG_PREFIX}${CLOSE_DIALOG_EVENT}`,
            payload: `Closed customer details dialog for asset ID: ${assetId}`,
        });
    };

    if (isLoading || isFetching) {
        return <DialogLoadingState onClose={handleCloseDialog} />;
    }

    if (isError || !assignment) {
        return <DialogErrorState onClose={handleCloseDialog} refetch={refetch} />;
    }

    const { fleet_account: fleetAccount, contact_person: contactPerson } = assignment;
    const address = fleetAccount.address;
    const countryCode = address?.country_code ?? '';

    return (
        <div className="bg-white min-height-200">
            <Dialog.Title title={fleetAccount.name ?? ''} onCloseButtonClick={handleCloseDialog} />

            <Dialog.Body className="padding-15 margin-left-15">
                <div className="row col-xs-6">
                    <div className="margin-top-10 margin-bottom-10">
                        <div className="text-color-gray margin-top-10">
                            <FormattedMessage id="intl-msg:engineOil.table.customerDetails.address" />
                        </div>
                        {renderAddressLine(address?.street ?? undefined)}
                        {address?.zip && address?.city
                            ? `${address.zip.trim()} ${address.city.trim()}`
                            : renderAddressLine(address?.city ?? undefined)}
                        {countryCode && (
                            <div>
                                <FormattedMessage
                                    id={`intl-msg:common.country.${countryCode}`}
                                    defaultMessage={countryCode}
                                />
                            </div>
                        )}
                    </div>
                    <div className="margin-top-10 margin-bottom-10">
                        <ContactPerson contactPerson={contactPerson} />
                    </div>
                </div>
            </Dialog.Body>

            <DialogFooter onClose={handleCloseDialog} />
        </div>
    );
};

export default CustomerDetailsDialog;
